import React, { useCallback, useMemo } from 'react';
import { css } from '@emotion/core';
import {
  CheckboxField,
  Table,
  TableColumnConfig,
  Text,
  useControlledField,
} from '@weave/design-system';
import { theme } from '@weave/theme-original';
import {
  UnifyLocationRow,
  UnifyLocationsTableProps,
} from './unify-locations-table.types';
import { PocDropdown } from './poc-dropdown';
import { AddEditNotes } from './add-edit-notes';

type UnifyTableColumn = TableColumnConfig<UnifyLocationRow> & {
  disableSortBy?: boolean;
};

interface RowCheckboxProps {
  locationId: string;
  locationName: string;
  selected: boolean;
  disabled: boolean;
  onToggle: (id: string) => void;
}

const RowCheckbox: React.FC<RowCheckboxProps> = ({
  locationId,
  locationName,
  selected,
  disabled,
  onToggle,
}) => {
  const fieldProps = useControlledField({
    type: 'checkbox',
    value: selected,
    onChange: () => onToggle(locationId),
  });

  return (
    <div css={styles.checkboxCell}>
      <CheckboxField
        {...fieldProps}
        name={`select-location-${locationId}`}
        label=""
        aria-label={`Select ${locationName}`}
        disabled={disabled}
      />
    </div>
  );
};

export const UnifyLocationsTable: React.FC<UnifyLocationsTableProps> = ({
  locations,
  mode,
  selectedLocationIds = [],
  onSelectionChange,
  maxSelections = 5,
  locationDetailsMap,
  onPocChange,
  onNotesChange,
  showValidationErrors = false,
  isPaginated,
  pageSize = 10,
}) => {
  const isSelected = useCallback(
    (locationId: string) => selectedLocationIds.includes(locationId),
    [selectedLocationIds]
  );

  const handleRowToggle = useCallback(
    (locationId: string) => {
      const selected = isSelected(locationId);
      if (selected) {
        onSelectionChange?.(selectedLocationIds.filter((id) => id !== locationId));
      } else {
        if (selectedLocationIds.length < maxSelections) {
          onSelectionChange?.([...selectedLocationIds, locationId]);
        }
      }
    },
    [isSelected, maxSelections, onSelectionChange, selectedLocationIds]
  );

  const columns = useMemo<UnifyTableColumn[]>(() => {
    return [
      {
        Header: '',
        id: 'select',
        omit: mode !== 'selection',
        accessor: (row) => row,
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        disableSortBy: true,
        cellRenderer: (row: UnifyLocationRow) => {
          const selected = isSelected(row.locationId);
          const disabled = !selected && selectedLocationIds.length >= maxSelections;
          return (
            <RowCheckbox
              locationId={row.locationId}
              locationName={row.locationName}
              selected={selected}
              disabled={disabled}
              onToggle={handleRowToggle}
            />
          );
        },
      },
      {
        Header: 'Location',
        id: 'location',
        accessor: (row) => row.locationName,
        cellRenderer: (locationName: string) => (
          <Text size="medium" as="div">
            {locationName}
          </Text>
        ),
        disableSortBy: true,
      },
      {
        Header: 'ID/Slug',
        id: 'idSlug',
        accessor: (row) => row,
        cellRenderer: (row: UnifyLocationRow) => (
          <div css={styles.idSlugContainer}>
            <Text size="small" color="light" as="div">
              {row.locationId}
            </Text>
            <Text size="small" color="light" as="div">
              {row.slug}
            </Text>
          </div>
        ),
        disableSortBy: true,
      },
      {
        Header: mode === 'snapshot' ? 'Point of Contact' : 'Point of Contact*',
        id: 'poc',
        accessor: (row) => row,
        cellRenderer: (row: UnifyLocationRow) => {
          const details = locationDetailsMap[row.locationId];
          const selected = mode === 'selection' ? isSelected(row.locationId) : true;

          if (mode === 'snapshot') {
            const pocName = details?.pocDisplayName;
            const pocEmail = details?.pocEmail;
            if (!pocName && !pocEmail) {
              return (
                <Text size="medium" color="light">
                  —
                </Text>
              );
            }
            return (
              <div css={styles.pocSnapshotCell}>
                {pocName && (
                  <Text size="medium" weight="bold" as="div">
                    {pocName}
                  </Text>
                )}
                {pocEmail && (
                  <Text size="small" color="light" as="div">
                    {pocEmail}
                  </Text>
                )}
              </div>
            );
          }

          return (
            <PocDropdown
              locationId={row.locationId}
              value={details?.pocEmail || ''}
              onChange={(email) => onPocChange?.(row.locationId, email)}
              showValidationErrors={showValidationErrors}
              disabled={!selected}
            />
          );
        },
        disableSortBy: true,
      },
      {
        Header: 'Notes',
        id: 'notes',
        accessor: (row) => row,
        cellRenderer: (row: UnifyLocationRow) => {
          const details = locationDetailsMap[row.locationId];
          const selected = mode === 'selection' ? isSelected(row.locationId) : true;

          return (
            <AddEditNotes
              locationId={row.locationId}
              notes={details?.notes}
              onNotesChange={(notes) => onNotesChange?.(row.locationId, notes)}
              readOnly={mode === 'snapshot'}
              disabled={!selected}
            />
          );
        },
        disableSortBy: true,
      },
    ];
  }, [
    mode,
    locationDetailsMap,
    selectedLocationIds,
    maxSelections,
    onPocChange,
    onNotesChange,
    showValidationErrors,
    isSelected,
    handleRowToggle,
  ]);

  const maxSelectable = Math.min(maxSelections, locations.length);
  const allSelected = maxSelectable > 0 && selectedLocationIds.length >= maxSelectable;
  const someSelected =
    selectedLocationIds.length > 0 && selectedLocationIds.length < maxSelectable;

  const handleSelectAllToggle = useCallback(() => {
    if (allSelected) {
      onSelectionChange?.([]);
    } else {
      const toSelect = locations.slice(0, maxSelectable).map((loc) => loc.locationId);
      onSelectionChange?.(toSelect);
    }
  }, [allSelected, locations, maxSelectable, onSelectionChange]);

  const selectAllFieldProps = useControlledField({
    type: 'checkbox',
    value: allSelected || someSelected,
    onChange: handleSelectAllToggle,
  });

  const selectAllLabel =
    selectedLocationIds.length > 0
      ? `Selected ${selectedLocationIds.length} of ${maxSelectable}`
      : `Select All (up to ${maxSelectable})`;

  const shouldPaginate = isPaginated ?? locations.length > pageSize;

  return (
    <div css={styles.tableWrapper}>
      {mode === 'selection' && (
        <div css={styles.selectionHeader}>
          <CheckboxField
            {...selectAllFieldProps}
            name="unify-locations__select-all"
            indeterminate={someSelected}
            disabled={locations.length === 0}
            label={selectAllLabel}
          />
        </div>
      )}
      <Table
        colConfig={columns}
        data={locations}
        hasResponsiveColWidths
        isPaginated={shouldPaginate}
        clientPaginationConfig={
          shouldPaginate
            ? {
                defaultRowsPerPage: pageSize,
                rowsPerPageOptions: [10, 25, 50],
              }
            : undefined
        }
        uniqueRowId={(row) => row.locationId}
        disableMultiSort
        tableStyle={styles.table}
      />
    </div>
  );
};

const styles = {
  tableWrapper: css`
    background-color: #ffffff;
    overflow: hidden;
  `,
  selectionHeader: css`
    display: flex;
    align-items: center;
    padding: ${theme.spacing(2, 2.5)};

    > div {
      margin-bottom: 0;
    }
  `,
  checkboxCell: css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    > div {
      margin-bottom: 0;
    }
  `,
  table: css`
    margin: 0;
    border: 1px solid #e8e8e8;
    border-radius: 6px;

    [role='columnheader'] {
      background-color: #ffffff;
      color: #60687a;
      font-size: 14px;
      font-weight: 700;
      padding: ${theme.spacing(2, 2.5)};
      border-bottom: 1px solid #e8e8e8;
    }

    [role='cell'] {
      padding: ${theme.spacing(2, 2.5)};
      vertical-align: middle;
      border-bottom: 1px solid #f0f0f0;
    }

    [role='row']:last-of-type [role='cell'] {
      border-bottom: 0;
    }
  `,
  idSlugContainer: css`
    display: flex;
    flex-direction: column;
  `,
  pocSnapshotCell: css`
    display: flex;
    flex-direction: column;
    gap: 2px;
  `,
};
